import * as yup from 'yup'
import { defineMessages, IntlShape } from 'react-intl'
import { isEmpty } from 'lodash'

const companyMessages = defineMessages({
  required: {
    id: 'sites.form.company.required',
    defaultMessage: 'The company is required'
  }
})

const nameMessages = defineMessages({
  required: {
    id: 'sites.form.name.required',
    defaultMessage: 'The site name is required'
  },
  max: {
    id: 'sites.form.name.max',
    defaultMessage: 'The site name can be up to 255 characters'
  }
})

const typeMessages = defineMessages({
  required: {
    id: 'sites.form.type.required',
    defaultMessage: 'The type is required'
  }
})

const paymentMessages = defineMessages({
  required: {
    id: 'sites.form.payment.required',
    defaultMessage: 'You have to choose one payment method at least'
  }
})

const companyLinesMessages = defineMessages({
  required: {
    id: 'sites.form.companyLines.required',
    defaultMessage: 'You have to choose one company line at least'
  }
})

const line1Messages = defineMessages({
  required: {
    id: 'sites.new.form.address.line1.required',
    defaultMessage: 'The line 1 is required'
  },
  max: {
    id: 'sites.new.form.address.line1.max',
    defaultMessage: 'The line 1 can be up to 255 characters'
  },
  min: {
    id: 'sites.new.form.address.line1.min',
    defaultMessage: 'The line 1 should have at least 2 characters'
  }
})

const line2Messages = defineMessages({
  max: {
    id: 'sites.new.form.address.line2.max',
    defaultMessage: 'The information can be up to 255 characters'
  }
})

const cityMessages = defineMessages({
  required: {
    id: 'sites.new.form.address.city.required',
    defaultMessage: 'The city is required'
  },
  max: {
    id: 'sites.new.form.address.city.max',
    defaultMessage: 'The city can be up to 255 characters'
  },
  min: {
    id: 'sites.new.form.address.city.min',
    defaultMessage: 'The city should have at least 2 characters'
  }
})

const zipCodeMessages = defineMessages({
  required: {
    id: 'sites.new.form.address.zipCode.required',
    defaultMessage: 'The ZIP code is required'
  },
  regex: {
    id: 'sites.new.form.address.zipCode.regex',
    defaultMessage: 'The format for a ZIP code is wrong'
  }
})

const stateMessages = defineMessages({
  required: {
    id: 'sites.new.form.address.state.required',
    defaultMessage: 'The state is required'
  }
})

const testRequired = (value: string | object, isEdit?: boolean): boolean => {
  if (!isEdit) {
    return !isEmpty(value)
  }

  return true
}

export const schema = (intl: IntlShape, isEdit?: boolean): yup.AnyObjectSchema => {
  return yup.object().shape({
    company: yup.object().shape({
      id: yup
        .string()
        .test('is-company-required', intl.formatMessage(companyMessages.required), function (value = ''): boolean {
          return testRequired(value, isEdit)
        })
    }),
    name: yup
      .string()
      .required(intl.formatMessage(nameMessages.required))
      .max(255, intl.formatMessage(nameMessages.max)),
    type: yup.string().required(intl.formatMessage(typeMessages.required)),
    settings: yup.object().shape({
      payment: yup.array().min(1, intl.formatMessage(paymentMessages.required)),
      enabledCompanyLineIDs: yup.array().min(1, intl.formatMessage(companyLinesMessages.required))
    }),
    address: yup.object().shape(
      {
        line1: yup
          .string()
          .when(['city', 'zipCode', 'stateCode', 'line2'], {
            is: (city: string, zipCode: string, stateCode: string, line2: string) => {
              return city || zipCode || stateCode || line2
            },
            then: yup
              .string()
              .min(2, intl.formatMessage(line1Messages.min))
              .required(intl.formatMessage(line1Messages.required))
          })
          .max(255, intl.formatMessage(line1Messages.max)),
        line2: yup.string().max(255, intl.formatMessage(line2Messages.max)),
        city: yup
          .string()
          .when(['line1', 'zipCode', 'stateCode', 'line2'], {
            is: (line1: string, zipCode: string, stateCode: string, line2: string) => {
              return line1 || zipCode || stateCode || line2
            },
            then: yup
              .string()
              .min(2, intl.formatMessage(cityMessages.min))
              .required(intl.formatMessage(cityMessages.required))
          })
          .max(255, intl.formatMessage(cityMessages.max)),
        zipCode: yup
          .string()
          .when(['line1', 'city', 'stateCode', 'line2'], {
            is: (line1: string, city: string, stateCode: string, line2: string) => {
              return line1 || city || stateCode || line2
            },
            then: yup.string().required(intl.formatMessage(zipCodeMessages.required))
          })
          .matches(/^([a-za-z0-9]{5,15})*$/, intl.formatMessage(zipCodeMessages.regex)),
        stateCode: yup.string().when(['line1', 'city', 'zipCode', 'line2'], {
          is: (line1: string, city: string, zipCode: string, line2: string) => {
            return line1 || city || zipCode || line2
          },
          then: yup.string().required(intl.formatMessage(stateMessages.required))
        })
      },
      [
        ['line1', 'city'],
        ['line1', 'zipCode'],
        ['line1', 'stateCode'],
        ['line1', 'line2'],
        ['city', 'zipCode'],
        ['city', 'stateCode'],
        ['city', 'line2'],
        ['zipCode', 'stateCode'],
        ['zipCode', 'line2'],
        ['stateCode', 'line2']
      ]
    )
  })
}
